import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Flame } from 'lucide-react';
import { signInWithMagicLink } from '../lib/auth';
import Button from '../components/ui/Button';
import { toast } from '../lib/toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageError, setAgeError] = useState(false);

  // Clear age error when switching between login/signup
  useEffect(() => {
    if (!isSignUp) {
      setAgeError(false);
    }
  }, [isSignUp]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      // Block signup if age not confirmed
      if (isSignUp && !ageConfirmed) {
        setAgeError(true);
        toast.error('Confirm legal age');
        setLoading(false);
        return;
      }
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: window.location.origin
          }
        });
        if (error) throw error;
        setMessage('Check your email for the confirmation link!');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      paddingBottom: '80px',
    }}>
      <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Flame size={64} color="var(--color-accent)" style={{ margin: '0 auto' }} />
          <h1 style={{ fontSize: '3rem', marginTop: '1rem', marginBottom: '0.5rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            Ember
          </h1>
          <p style={{ color: '#cfcfcf', fontSize: '1rem' }}>
            The social app for pipe & cigar enthusiasts
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />

          {isSignUp && (
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              fontSize: '0.95rem', 
              color: ageError ? 'var(--color-danger)' : '#cfcfcf',
              transition: 'color 0.2s ease'
            }}>
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => {
                  setAgeConfirmed(e.target.checked);
                  if (e.target.checked) setAgeError(false);
                }}
                style={{ 
                  width: 18, 
                  height: 18,
                  accentColor: ageError ? 'var(--color-danger)' : 'var(--color-accent)',
                  cursor: 'pointer'
                }}
              />
              <span>By continuing, you confirm you are of legal smoking age in your country</span>
            </label>
          )}

          {error && (
            <div style={{ color: '#ef4444', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {message && (
            <div style={{ color: 'var(--color-success)', fontSize: '0.875rem' }}>
              {message}
            </div>
          )}

          <Button 
            type="submit" 
            variant="primary" 
            loading={loading}
            disabled={loading}
            fullWidth
          >
            {isSignUp ? 'Sign Up' : 'Log In'}
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={() => setIsSignUp(!isSignUp)}
            fullWidth
          >
            {isSignUp ? 'Already have an account? Log In' : "Don't have an account? Sign Up"}
          </Button>
        </form>

        {/* Magic Link */}
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ color: '#d3de5e', marginBottom: '0.5rem' }}>Or get a magic link:</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="email"
              placeholder="Email for magic link"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              type="button"
              variant="secondary"
              loading={loading}
              onClick={async () => {
                try {
                  setLoading(true);
                  await signInWithMagicLink(magicEmail);
                  setMessage('Magic link sent. Check your email.');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to send magic link');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Send Link
            </Button>
          </div>
        </div>

        {/* Copyright & Ownership Notice (Beta Version) */}
        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: '#d3de5e' }}>Copyright & Ownership Notice (Beta Version)</h4>
          <div style={{ fontSize: '0.85rem', color: '#cfcfcf', lineHeight: 1.5 }}>
            <p style={{ marginBottom: '0.35rem' }}>Ember © 2025. All Rights Reserved.</p>
            <p style={{ marginBottom: '0.35rem' }}>This app and its content — including design, features, names, and code — are the property of its creator and are provided for private testing and feedback only.</p>
            <p style={{ marginBottom: '0.35rem' }}>Copying, redistributing, or reproducing any part of this app without written permission is strictly prohibited.</p>
            <p style={{ marginBottom: '0.35rem' }}>Unauthorized use or duplication of this software may violate copyright, trademark, and other applicable laws.</p>
            <p style={{ marginBottom: 0 }}>Ember is currently in a closed beta stage and may include experimental features subject to change or removal at any time.</p>
          </div>

          {/* Data & Privacy */}
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: '#d3de5e' }}>Data & Privacy</h4>
            <div style={{ fontSize: '0.85rem', color: '#cfcfcf', lineHeight: 1.5 }}>
              <p style={{ marginBottom: '0.35rem' }}>Ember collects your email address and profile information solely for authentication and app functionality.</p>
              <p style={{ marginBottom: '0.35rem' }}>No personal data is sold, shared, or used for marketing.</p>
              <p style={{ marginBottom: '0.35rem' }}>You can request deletion of your account and all data at any time by contacting the developer.</p>
              <p style={{ marginBottom: 0 }}>All user accounts and data are securely stored by Supabase, which provides industry-standard encryption and authentication.</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

